import { invalidate } from '@react-three/fiber';
import { Quaternion, Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid';

import {
    ArticleGroupPlacementLifeCycleCode,
    type ArticleGroupPlacement,
    type ArticleInstancePlacement,
    type BasePointOfInterest,
    type PartPlacement,
    type Point3D,
    type PlanarFacePOI
} from 'api/models';
import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import { updateFullQuaternion } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { preUpdateInstanceToArticleIdMap } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { decomposeRollAroundAxis } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID, EPSILON } from 'shared/constants';

import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';
import type { ArticleClipboardEntry } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleClipboardEntry';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

const previousQuaternion = new Quaternion();
const currentQuaternion = new Quaternion();
const anchorDeltaQuaternion = new Quaternion();
const partQuaternion = new Quaternion();
const alignmentQuaternion = new Quaternion();
const rollQuaternion = new Quaternion();
const partDeltaQuaternion = new Quaternion();
const alignment = new Vector3();
const pivotFrom = new Vector3();
const pivotTo = new Vector3();
const prevAnchorPoint = new Vector3();
const nextAnchorPoint = new Vector3();
const partPosition = new Vector3();

export function getPoiNormal(poi: BasePointOfInterest | undefined): Vector3 | null {
    if (!poi) {
        return null;
    }
    const formFaceNormal = (poi as PlanarFacePOI).formFace?.normal;
    if (!formFaceNormal) {
        return null;
    }
    return new Vector3(formFaceNormal.x, formFaceNormal.y, formFaceNormal.z);
}

export function getAnchorPartPosition(clipboard: ArticleClipboardEntry[]): Vector3 | null {
    if (clipboard.length > 0 && clipboard[0].parts.length > 0) {
        const firstPart = clipboard[0].parts[0];
        return new Vector3(firstPart.position.x, firstPart.position.y, firstPart.position.z);
    }
    return null;
}

export function getClipboardCentroid(clipboard: ArticleClipboardEntry[]): Vector3 {
    let partCount = 0;
    const centroid = new Vector3(0, 0, 0);
    clipboard.forEach((entry) => {
        entry.parts.forEach((part) => {
            centroid.x += part.position.x;
            centroid.y += part.position.y;
            centroid.z += part.position.z;
            partCount++;
        });
    });
    if (partCount > 0) {
        centroid.divideScalar(partCount);
    }
    return centroid;
}

export function getInsertionPoint(state: PlacementState, anchorPartPos: Vector3 | null, centroid: Vector3): Vector3 {
    if (state.pointerPositionRef.current) {
        return state.pointerPositionRef.current.clone();
    } else if (anchorPartPos) {
        return anchorPartPos.clone();
    } else {
        return centroid.clone();
    }
}

export function getPasteOffset(anchorPartPos: Vector3 | null, insertionPoint: Vector3, centroid: Vector3): Vector3 {
    if (anchorPartPos) {
        return new Vector3().subVectors(insertionPoint, anchorPartPos);
    } else {
        return new Vector3().subVectors(insertionPoint, centroid);
    }
}

function clonePartPlacement(part: PartPlacement): PartPlacement {
    return {
        name: part.name,
        position: { x: part.position.x, y: part.position.y, z: part.position.z },
        alignment: { x: part.alignment.x, y: part.alignment.y, z: part.alignment.z },
        rotation: part.rotation,
        scale: { x: part.scale.x, y: part.scale.y, z: part.scale.z }
    };
}

export function clonePartPlacementsWithZOffset(parts: PartPlacement[], zOffset: number): PartPlacement[] {
    return parts.map((part) => ({
        ...clonePartPlacement(part),
        position: { x: part.position.x, y: part.position.y, z: part.position.z + zOffset }
    }));
}

export function getPlacementAnchorPart(
    parts: PartPlacement[],
    referencePart?: PartPlacement
): PartPlacement | undefined {
    return (referencePart && parts.find((part) => part.name === referencePart.name)) ?? parts[0];
}

function updateQuaternionFromPlacement(part: PartPlacement, target: Quaternion): void {
    alignment.set(part.alignment.x, part.alignment.y, part.alignment.z);
    updateFullQuaternion(alignment, part.rotation, target);
}

function resolveCornerPartTransform(
    previousAnchorPart: PartPlacement | undefined,
    currentAnchorPart: PartPlacement | undefined,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    fallbackDelta: Quaternion
): Quaternion {
    const partDelta = partDeltaQuaternion;

    if (!previousAnchorPart || !currentAnchorPart) {
        pivotFrom.copy(previousAnchorPoint);
        pivotTo.copy(currentAnchorPoint);
        return partDelta.copy(fallbackDelta);
    }

    updateQuaternionFromPlacement(previousAnchorPart, previousQuaternion);
    updateQuaternionFromPlacement(currentAnchorPart, currentQuaternion);
    partDelta.copy(previousQuaternion).invert();
    partDelta.premultiply(currentQuaternion);
    pivotFrom.set(previousAnchorPart.position.x, previousAnchorPart.position.y, previousAnchorPart.position.z);
    pivotTo.set(currentAnchorPart.position.x, currentAnchorPart.position.y, currentAnchorPart.position.z);

    return partDelta;
}

function applyCornerPartTransform(
    part: PartPlacement,
    previousAnchorPart: PartPlacement | undefined,
    currentAnchorPart: PartPlacement | undefined,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    fallbackDelta: Quaternion
): void {
    const partDelta = resolveCornerPartTransform(
        previousAnchorPart,
        currentAnchorPart,
        previousAnchorPoint,
        currentAnchorPoint,
        fallbackDelta
    );

    updateQuaternionFromPlacement(part, partQuaternion);
    partQuaternion.premultiply(partDelta);

    partPosition
        .set(part.position.x, part.position.y, part.position.z)
        .sub(pivotFrom)
        .applyQuaternion(partDelta)
        .add(pivotTo);
    part.position = { x: partPosition.x, y: partPosition.y, z: partPosition.z };

    alignment.set(part.alignment.x, part.alignment.y, part.alignment.z).applyQuaternion(partDelta).normalize();
    part.alignment = { x: alignment.x, y: alignment.y, z: alignment.z };

    updateFullQuaternion(alignment, 0, alignmentQuaternion);
    rollQuaternion.copy(alignmentQuaternion).invert();
    rollQuaternion.premultiply(partQuaternion);
    part.rotation = decomposeRollAroundAxis(rollQuaternion, alignment);
}

export function applyCornerPaste(
    payload: ArticleGroupPlacement[],
    anchorArticleId: string,
    candidates: PartPlacement[]
): void {
    const anchorGroup = payload.find((group) => group.articleGuid === anchorArticleId) ?? payload[0];
    const anchorInstance = anchorGroup?.instances[0];
    if (!anchorInstance) {
        return;
    }

    const previousAnchorParts = anchorInstance.parts.map(clonePartPlacement);
    const currentAnchorParts = candidates.map(clonePartPlacement);
    const previousAnchorPart = getPlacementAnchorPart(previousAnchorParts);
    const currentAnchorPart = getPlacementAnchorPart(currentAnchorParts, previousAnchorPart);
    if (!previousAnchorPart || !currentAnchorPart) {
        return;
    }

    updateQuaternionFromPlacement(previousAnchorPart, previousQuaternion);
    updateQuaternionFromPlacement(currentAnchorPart, currentQuaternion);
    anchorDeltaQuaternion.copy(previousQuaternion).invert();
    anchorDeltaQuaternion.premultiply(currentQuaternion);

    prevAnchorPoint.set(previousAnchorPart.position.x, previousAnchorPart.position.y, previousAnchorPart.position.z);
    nextAnchorPoint.set(currentAnchorPart.position.x, currentAnchorPart.position.y, currentAnchorPart.position.z);

    anchorInstance.parts = currentAnchorParts;

    const previousPartsByName = new Map(previousAnchorParts.map((part) => [part.name, part]));
    const currentPartsByName = new Map(currentAnchorParts.map((part) => [part.name, part]));
    for (const group of payload) {
        for (const instance of group.instances) {
            if (instance === anchorInstance) {
                continue;
            }

            for (const part of instance.parts) {
                applyCornerPartTransform(
                    part,
                    previousPartsByName.get(part.name),
                    currentPartsByName.get(part.name),
                    prevAnchorPoint,
                    nextAnchorPoint,
                    anchorDeltaQuaternion
                );
            }
        }
    }
}

export function isMultiPOISelection(clipboard: ArticleClipboardEntry[]): boolean {
    const poiScopes = new Set(
        clipboard.map((entry) => [...new Set(entry.poiGuids)].sort().join('|'))
    );
    return poiScopes.size > 1;
}

export function computeReorientedQuaternion(
    entry: ArticleClipboardEntry,
    targetPoiNormal: Vector3 | null
): Quaternion | null {
    if (entry.poiNormal && targetPoiNormal) {
        const from = new Vector3(entry.poiNormal.x, entry.poiNormal.y, entry.poiNormal.z).normalize();
        const to = targetPoiNormal.clone().normalize();
        if (from.dot(to) < 1 - EPSILON) {
            return new Quaternion().setFromUnitVectors(from, to);
        }
    }
    return null;
}

export function createInstancesForPaste(
    groupEntries: ArticleClipboardEntry[],
    isMultiPOISelection: boolean,
    isZAxisOnlyPaste: boolean,
    anchorPartPos: Vector3 | null,
    insertionPoint: Vector3,
    offset: Vector3,
    targetPoiNormal: Vector3 | null,
    selectedPoiInstanceIds: string[],
    targetPoiGuids?: string[]
): ArticleInstancePlacement[] {
    return groupEntries.map((entry) => {
        const id = uuidv4();
        selectedPoiInstanceIds.push(id);
        const poiGuids = targetPoiGuids ?? entry.poiGuids;

        // Preserve the source layout and apply only the cursor's z displacement.
        if (isZAxisOnlyPaste) {
            return {
                id,
                poiGuids: [...poiGuids],
                isNewPlacement: true,
                isLocked: false,
                sourceType: undefined,
                sourceGroupId: null,
                sourceGroupName: null,
                sourceGroupInstanceId: null,
                sourceSavedGroupPrefabId: null,
                parts: entry.parts.map((part) => ({
                    name: part.name,
                    position: {
                        x: part.position.x,
                        y: part.position.y,
                        z: part.position.z + offset.z
                    },
                    alignment: { x: part.alignment.x, y: part.alignment.y, z: part.alignment.z },
                    rotation: part.rotation,
                    scale: { x: part.scale.x, y: part.scale.y, z: part.scale.z }
                }))
            };
        }

        // Multi POI paste
        if (isMultiPOISelection) {
            return {
                id,
                poiGuids: [...entry.poiGuids],
                isNewPlacement: true,
                isLocked: false,
                sourceType: undefined,
                sourceGroupId: null,
                sourceGroupName: null,
                sourceGroupInstanceId: null,
                sourceSavedGroupPrefabId: null,
                parts: entry.parts.map((part) => ({
                    name: part.name,
                    position: { x: part.position.x, y: part.position.y, z: part.position.z },
                    alignment: { x: part.alignment.x, y: part.alignment.y, z: part.alignment.z },
                    rotation: part.rotation,
                    scale: { x: part.scale.x, y: part.scale.y, z: part.scale.z }
                }))
            };
        }

        // Single POI paste
        const entryReorientQuat = computeReorientedQuaternion(entry, targetPoiNormal);
        return {
            id,
            poiGuids: [EMPTY_GUID],
            isNewPlacement: true,
            isLocked: false,
            sourceType: undefined,
            sourceGroupId: null,
            sourceGroupName: null,
            sourceGroupInstanceId: null,
            sourceSavedGroupPrefabId: null,
            parts: entry.parts.map((part) => {
                // Rotate alignment by the reorientation quaternion.
                const newAlignment = new Vector3(part.alignment.x, part.alignment.y, part.alignment.z);
                if (entryReorientQuat) {
                    newAlignment.applyQuaternion(entryReorientQuat);
                }

                // Compute position: rotate the relative offset from the anchor instance, then place at the insertion point.
                let newPosition: { x: number; y: number; z: number };
                if (anchorPartPos) {
                    const relOffset = new Vector3(
                        part.position.x - anchorPartPos.x,
                        part.position.y - anchorPartPos.y,
                        part.position.z - anchorPartPos.z
                    );
                    if (entryReorientQuat) {
                        relOffset.applyQuaternion(entryReorientQuat);
                    }
                    newPosition = {
                        x: insertionPoint.x + relOffset.x,
                        y: insertionPoint.y + relOffset.y,
                        z: insertionPoint.z + relOffset.z
                    };
                } else {
                    newPosition = {
                        x: part.position.x + offset.x,
                        y: part.position.y + offset.y,
                        z: part.position.z + offset.z
                    };
                }

                return {
                    name: part.name,
                    position: newPosition,
                    alignment: { x: newAlignment.x, y: newAlignment.y, z: newAlignment.z },
                    rotation: part.rotation,
                    scale: { x: part.scale.x, y: part.scale.y, z: part.scale.z }
                };
            })
        };
    });
}

export function collectPastedGroupMembers(
    groupEntries: ArticleClipboardEntry[],
    instances: ArticleInstancePlacement[],
    articleId: string,
    pastedGroupMembers: Map<string, { name: string; members: Array<{ articleId: string; instanceId: string }> }>
) {
    groupEntries.forEach((entry, i) => {
        if (entry.clipboardGroupId && instances[i].id) {
            const existing = pastedGroupMembers.get(entry.clipboardGroupId) ?? {
                name: entry.clipboardGroupName ?? 'Group',
                members: []
            };
            existing.members.push({ articleId, instanceId: instances[i].id! });
            pastedGroupMembers.set(entry.clipboardGroupId, existing);
        }
    });
}

export function buildPastePayload(
    clipboard: ArticleClipboardEntry[],
    articles: Record<string, Article>,
    anchorPartPos: Vector3 | null,
    insertionPoint: Vector3,
    offset: Vector3,
    targetPoiNormal: Vector3,
    isMultiPOISelection: boolean,
    isZAxisOnlyPaste: boolean,
    targetPoiGuids?: string[]
) {
    const payload: ArticleGroupPlacement[] = [];
    const selectedPoiInstanceIds: string[] = [];
    const articleInstanceIds = new Set<string>();
    const grouped = new Map<string, ArticleClipboardEntry[]>();
    const pastedGroupMembers = new Map<
        string,
        { name: string; members: Array<{ articleId: string; instanceId: string }> }
    >();

    for (const entry of clipboard) {
        let group = grouped.get(entry.articleId);
        if (!group) {
            group = [];
            grouped.set(entry.articleId, group);
        }
        group.push(entry);
    }

    for (const [articleId, groupEntries] of grouped) {
        const article = articles[articleId];
        if (article) {
            const instances = createInstancesForPaste(
                groupEntries,
                isMultiPOISelection,
                isZAxisOnlyPaste,
                anchorPartPos,
                insertionPoint,
                offset,
                targetPoiNormal,
                selectedPoiInstanceIds,
                targetPoiGuids
            );

            for (const instance of instances) {
                if (instance.id) {
                    articleInstanceIds.add(instance.id);
                }
            }

            collectPastedGroupMembers(groupEntries, instances, articleId, pastedGroupMembers);

            payload.push({
                articleGuid: articleId,
                articleNumber: article.articleNumber,
                lifeCycleCode: article.lifeCycleCode as ArticleGroupPlacementLifeCycleCode,
                representationGuid: articleId,
                instances
            });
        }
    }

    return {
        payload,
        selectedPoiInstanceIds,
        articleInstanceIds,
        pastedGroupMembers
    };
}

export function finalizePaste(
    state: PlacementState,
    payload: ArticleGroupPlacement[],
    articleInstanceIds: Set<string>,
    selectedPoiInstanceIds: string[],
    pastedGroupMembers: Map<string, { name: string; members: Array<{ articleId: string; instanceId: string }> }>,
    isZAxisDragActive: boolean
) {
    if (payload.length === 0) {
        return;
    }

    const mapEntries = payload.flatMap((group) =>
        group.instances
            .filter((instance) => instance.id)
            .map((instance) => ({
                instanceId: instance.id!,
                articleId: group.articleGuid
            }))
    );

    preUpdateInstanceToArticleIdMap(mapEntries);
    state.addPlacementIndicator(payload);

    for (const [, groupData] of pastedGroupMembers) {
        if (groupData.members.length >= 2) {
            state.createArticleGroup(groupData.name, groupData.members);
        }
    }

    const selections = Object.fromEntries(selectedPoiInstanceIds.map((id) => [id, undefined]));

    state.setArticleInstanceSelections(selections);
    state.setIsDragActiveRef(true);
    state.setIsAutoDragActiveRef(true);
    state.setIsZAxisDragActiveRef(isZAxisDragActive);

    requestAnimationFrame(() => {
        const currentState = usePlacementStore.getState();
        if (currentState.isAutoDragActiveRef.current) {
            currentState.syncSelectedInstancesToCursor();
        }
        usePlacementStore.setState({
            placementUpdate: {
                event: PlacementUpdateEvent.PLACE,
                articleInstanceIds
            }
        });
        invalidate();
    });
}

export function getNearestSnapCandidate(
    candidates: PartPlacement[],
    targetPosition: Point3D | Vector3 | null | undefined
): PartPlacement | undefined {
    if (!targetPosition || candidates.length === 0) {
        return undefined;
    }

    let nearestCandidate: PartPlacement | undefined;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;

    for (const candidate of candidates) {
        const dx = candidate.position.x - targetPosition.x;
        const dy = candidate.position.y - targetPosition.y;
        const dz = candidate.position.z - targetPosition.z;

        const distanceSq = dx * dx + dy * dy + dz * dz;
        if (distanceSq < nearestDistanceSq) {
            nearestDistanceSq = distanceSq;
            nearestCandidate = candidate;
        }
    }

    return nearestCandidate;
}
